class GhCartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-gh-cart-discount-input]');
    this.status = this.querySelector('[data-gh-cart-discount-status]');
    this.toggle = this.querySelector('[data-gh-cart-discount-toggle]');
    this.removeButton = this.querySelector('[data-gh-cart-discount-remove]');

    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.toggleExpanded());
    }

    if (this.removeButton) {
      this.removeButton.addEventListener('click', () => this.applyCode(''));
    }

    this.querySelectorAll('[data-gh-cart-discount-apply]').forEach((button) => {
      button.addEventListener('click', () => {
        const code = button.getAttribute('data-gh-cart-discount-code');
        this.applyCode(code || undefined);
      });
    });

    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.applyCode();
      });
    }
  }

  toggleExpanded() {
    const expanded = this.getAttribute('data-expanded') === 'true';
    this.setAttribute('data-expanded', expanded ? 'false' : 'true');
    if (this.toggle) this.toggle.textContent = expanded ? 'Show all' : 'Hide';
  }

  async applyCode(nextCode) {
    if (this.isApplying) return;

    const code = String(nextCode === undefined ? this.input?.value || '' : nextCode).trim();

    if (nextCode === undefined && !code) {
      this.showStatus('Enter a discount code.', 'error');
      this.input?.focus();
      return;
    }

    this.isApplying = true;
    this.querySelectorAll('[data-gh-cart-discount-apply], [data-gh-cart-discount-remove]').forEach((button) => {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    });
    this.showStatus('', 'loading');

    try {
      const sectionIds = window.location.pathname === Shopify.routes.cart
        ? ['cart-drawer', 'cart-desktop', 'cart-mobile']
        : ['cart-drawer'];
      const response = await fetch(`${Shopify.routes.cart_update}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: code,
          sections: sectionIds,
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      });

      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to apply this code.');

      const normalizedCode = code.toLowerCase();
      const codeResult = (cart.discount_codes || []).find(
        (discount) => String(discount.code || '').toLowerCase() === normalizedCode
      );
      const applicationResult = (cart.cart_level_discount_applications || []).some(
        (discount) => discount.type === 'discount_code'
          && String(discount.title || '').toLowerCase() === normalizedCode
      );
      const applied = !code || (codeResult ? codeResult.applicable !== false : applicationResult);
      const renderedSections = cart.sections || {};
      let updatedSection = false;

      Object.entries(renderedSections).forEach(([sectionId, sectionHtml]) => {
        if (!sectionHtml) return;
        updatedSection = true;
        window.dispatchEvent(new CustomEvent(`theme:cart:${sectionId.replace(/^cart-/, '')}:updateHtml`, {
          detail: { section: sectionHtml, load_transitions: false }
        }));
      });

      if (!updatedSection) {
        window.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      }

      requestAnimationFrame(() => {
        document.querySelectorAll('gh-cart-discount').forEach((current) => {
          const currentInput = current.querySelector('[data-gh-cart-discount-input]');
          if (currentInput && code) currentInput.value = code;
          if (!code) return;
          current.showStatus(
            applied ? 'Discount code applied.' : 'This discount code is not valid.',
            applied ? 'success' : 'error'
          );
        });
      });
    } catch (error) {
      this.showStatus(error.message || 'Unable to update discount code.', 'error');
    } finally {
      this.isApplying = false;
      if (this.isConnected) {
        this.querySelectorAll('[data-gh-cart-discount-apply], [data-gh-cart-discount-remove]').forEach((button) => {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        });
      }
    }
  }

  showStatus(message, state) {
    this.dataset.state = state;
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('gh-cart-discount')) {
  customElements.define('gh-cart-discount', GhCartDiscount);
}

customElements.whenDefined('cart-element').then(() => {
  const CartElement = customElements.get('cart-element');
  if (CartElement.prototype.ghEmptyBodyFix) return;

  const swapInImages = CartElement.prototype.swapInImages;
  CartElement.prototype.swapInImages = function (currentBody, nextBody) {
    return swapInImages.call(this, currentBody, nextBody) || nextBody;
  };
  CartElement.prototype.ghEmptyBodyFix = true;
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-gh-cart-page-toggle]');
  if (!button) return;

  const sidebar = button.closest('.gh-cart-page__sidebar');
  if (!sidebar) return;

  event.preventDefault();
  const expanded = !sidebar.classList.contains('is-open');
  sidebar.classList.toggle('is-open', expanded);
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
});

window.addEventListener('theme:cart:updated', () => {
  document.querySelectorAll('.gh-mini-cart__count[data-cart-count], .gh-cart-page__count[data-cart-count]').forEach((badge) => {
    const count = Number.parseInt(badge.dataset.cartCount, 10) || 0;
    badge.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'} in bag`);
    if (badge.classList.contains('gh-cart-page__count')) badge.textContent = String(count);
  });
});
